import { useEffect, useRef } from 'react';

// A tiny document-head manager (replaces react-helmet, ~44 kB of source in
// main.js). Components describe the tags they want with useHead(); every
// mounted description is merged and written to <head> once per batch:
//
// - title: the last mounted description with a title wins;
// - meta: one per `name` / `property` (last wins);
// - link: one per rel + hreflang (last wins), e.g. canonical and alternates;
// - scripts (JSON-LD): every description's scripts, identical ones once.
//
// Only elements marked `data-head` are touched (public/index.html marks its
// default SEO tags so the first page replaces them). Unchanged tags stay in
// place, so a re-render writes nothing. <html lang/dir> is not managed here:
// LanguageContext owns it (a second writer made the browser restyle the
// whole page twice on every load).

export const HEAD_ATTR = 'data-head';

// id -> description. Ids are given during render, in tree order, so a later
// (or deeper) component wins, as with react-helmet.
const entries = new Map();
let nextId = 1;
let scheduled = false;

const metaKey = (m) => (m.name ? `name:${m.name}` : m.property ? `property:${m.property}` : null);
const linkKey = (l) => `${l.rel}|${l.hrefLang || ''}`;

/** The merged tags of every mounted description (exported for tests). */
export const mergeHead = (list) => {
  let title;
  const meta = new Map();
  const links = new Map();
  const scripts = [];
  list.forEach((desc) => {
    if (!desc) return;
    if (desc.title) title = desc.title;
    (desc.meta || []).forEach((m) => {
      const key = m && metaKey(m);
      if (key && m.content != null) { meta.delete(key); meta.set(key, m); }
    });
    (desc.links || []).forEach((l) => {
      if (l && l.rel && l.href) { const key = linkKey(l); links.delete(key); links.set(key, l); }
    });
    (desc.scripts || []).forEach((s) => { if (s && !scripts.includes(s)) scripts.push(s); });
  });
  return { title, meta: [...meta.values()], links: [...links.values()], scripts };
};

const keyOfElement = (el) => {
  const tag = el.tagName;
  if (tag === 'META') return metaKey({ name: el.getAttribute('name'), property: el.getAttribute('property') });
  if (tag === 'LINK') return `link:${linkKey({ rel: el.getAttribute('rel'), hrefLang: el.getAttribute('hreflang') })}`;
  if (tag === 'SCRIPT') return `script:${el.textContent}`;
  return null;
};

const setAttrs = (el, attrs) => {
  Object.entries(attrs).forEach(([name, value]) => {
    if (value == null) return;
    const v = String(value);
    if (el.getAttribute(name) !== v) el.setAttribute(name, v);
  });
};

const flush = () => {
  scheduled = false;
  if (typeof document === 'undefined' || !document.head) return;
  const list = [...entries.keys()].sort((a, b) => a - b).map((id) => entries.get(id));
  const { title, meta, links, scripts } = mergeHead(list);
  if (title && document.title !== title) document.title = title;

  // Managed elements now in <head>, by key (index.html defaults included).
  const current = new Map();
  document.head.querySelectorAll(`[${HEAD_ATTR}], [data-react-helmet]`).forEach((el) => {
    const key = keyOfElement(el);
    if (key && !current.has(key)) current.set(key, el);
    else el.remove();
  });

  const want = [];
  meta.forEach((m) => want.push([metaKey(m), 'meta', m.name ? { name: m.name, content: m.content } : { property: m.property, content: m.content }]));
  links.forEach((l) => want.push([`link:${linkKey(l)}`, 'link', { rel: l.rel, hreflang: l.hrefLang, href: l.href }]));
  scripts.forEach((s) => want.push([`script:${s}`, 'script', null, s]));

  const keep = new Set();
  want.forEach(([key, tag, attrs, text]) => {
    keep.add(key);
    let el = current.get(key);
    if (!el) {
      el = document.createElement(tag);
      if (tag === 'script') { el.type = 'application/ld+json'; el.textContent = text; }
      el.setAttribute(HEAD_ATTR, '');
      if (attrs) setAttrs(el, attrs);
      document.head.appendChild(el);
      return;
    }
    if (attrs) setAttrs(el, attrs);
  });
  current.forEach((el, key) => { if (!keep.has(key)) el.remove(); });
};

const schedule = () => {
  if (scheduled) return;
  scheduled = true;
  // One write for all descriptions changed in the same commit.
  Promise.resolve().then(flush);
};

/** Register, replace or (desc = null) drop the description `id`. */
export const setHead = (id, desc) => {
  if (desc) {
    entries.set(id, desc);
  } else {
    entries.delete(id);
  }
  schedule();
};

/** Writes the merged head now (tests). */
export const flushHead = flush;

/**
 * useHead({ title, meta: [{name|property, content}], links: [{rel, href, hrefLang}],
 * scripts: ['<json>'] }): the tags stay while the component is mounted.
 * `desc` = null/undefined adds nothing.
 */
export const useHead = (desc) => {
  const idRef = useRef(0);
  if (!idRef.current) idRef.current = nextId++;
  const signature = desc ? JSON.stringify(desc) : '';
  useEffect(() => {
    setHead(idRef.current, signature ? JSON.parse(signature) : null);
  }, [signature]);
  useEffect(() => {
    const id = idRef.current;
    return () => setHead(id, null);
  }, []);
};

/** <JsonLd json="…" />: one JSON-LD script in <head> while mounted. */
export const JsonLd = ({ json }) => {
  useHead(json ? { scripts: [json] } : null);
  return null;
};
